// The production plan (ADR-032; contracts 1.5): the segments a tenant has chosen to film, across
// guides, within a budget of video. The plan stores only what was picked, by whom and when; the
// seconds each segment counts come from the pack (D-75), so the summary is computed and never stored.
//
// Where each refusal lives. A plan alone has no runtimes, so its schema can refuse only what it can
// see: a duplicate segment. The two refusals that need the pack — a reference entry, and used seconds
// over the budget — live in validatePlan, which every writer calls before it stores a plan. A reader
// calls summarisePlan, which does not refuse an overrun: a stored plan can pass the budget when an
// estimate is replaced by a measured runtime, and it stays as it is (ADR-032 decision 2), so the screen
// can show the overrun as a negative remainingSeconds. While it is over, validatePlan, given the stored
// plan as `previous`, accepts a change that does not add seconds, so the tenant can remove or swap its
// way back down; a change that adds seconds must end within the budget.
import { z } from "zod";
import { Count, Id, IsoUtc, NonEmpty, strictObject, version } from "./primitives.js";
/** 50 minutes of video: the owner's product rule for the POC. Not tenant data (ADR-032 decision 1). */
export const PRODUCTION_BUDGET_SECONDS = 3000;
/** One chosen segment: a title in a pack. */
export const ProductionPlanSegment = strictObject({ packId: Id, titleId: Id });
/** The key a segment is unique by within a plan. */
export const segmentKey = (s) => `${s.packId}/${s.titleId}`;
function refuseDuplicates(segments, ctx, what) {
    const seen = new Set();
    segments.forEach((s, i) => {
        const key = segmentKey(s);
        if (seen.has(key))
            ctx.addIssue({
                code: "custom",
                message: `${what}: segment ${key} is chosen once`,
                path: ["segments", i],
            });
        seen.add(key);
    });
}
/** A tenant's current plan: what is picked, who picked it and when. */
export const ProductionPlan = strictObject({
    v: version(1),
    tenantId: Id,
    /** 1 on the first write, +1 per write: the precondition of the next one (contracts 1.5.3). */
    version: z.number().int().positive(),
    segments: z.array(ProductionPlanSegment),
    pickedBy: NonEmpty, // a principal's userId
    pickedAt: IsoUtc,
}).superRefine((p, ctx) => {
    refuseDuplicates(p.segments, ctx, "production plan");
});
/**
 * The body of PUT /v1/production-plan (contracts 1.5.3): the new selection and the `version` of the
 * plan the writer read. Absent or 0 means the writer read no plan; it is accepted only while the
 * tenant has none. Any other mismatch with the stored version is refused, writing nothing, as 409
 * http.conflict; the error body carries no details, so the client reads the plan again.
 */
export const ProductionPlanReplace = strictObject({
    version: Count.optional(),
    segments: z.array(ProductionPlanSegment),
});
/** Which runtime a segment counted: the measured one once present, else the estimate. */
export const SegmentRuntimeKind = z.enum(["measured", "estimated"]);
export const ProductionPlanSummarySegment = strictObject({
    packId: Id,
    titleId: Id,
    seconds: Count,
    runtime: SegmentRuntimeKind,
    blocked: z.array(Id), // ids of the open contradictions that refuse this segment's render (ADR-031)
});
/**
 * Computed from a plan and the pack; never stored. remainingSeconds is budgetSeconds − usedSeconds and
 * is negative when a stored plan has run over (ADR-032 decision 2).
 */
export const ProductionPlanSummary = strictObject({
    budgetSeconds: z.number().int().positive(),
    usedSeconds: Count,
    remainingSeconds: z.number().int(),
    segments: z.array(ProductionPlanSummarySegment),
}).superRefine((s, ctx) => {
    refuseDuplicates(s.segments, ctx, "production plan summary");
    const used = s.segments.reduce((n, seg) => n + seg.seconds, 0);
    if (s.usedSeconds !== used)
        ctx.addIssue({
            code: "custom",
            message: `production plan summary: usedSeconds is ${String(s.usedSeconds)} but the segments give ${String(used)}`,
            path: ["usedSeconds"],
        });
    if (s.remainingSeconds !== s.budgetSeconds - s.usedSeconds)
        ctx.addIssue({
            code: "custom",
            message: "production plan summary: remainingSeconds is budgetSeconds − usedSeconds",
            path: ["remainingSeconds"],
        });
});
/** Thrown by summarisePlan for a segment it cannot count. validatePlan returns these instead. */
export class ProductionPlanInvalid extends Error {
    errors;
    constructor(errors) {
        super(`production plan: ${errors.map((e) => e.code).join(", ")}`);
        this.errors = errors;
        this.name = "ProductionPlanInvalid";
    }
}
/** The segment errors a plan has without counting seconds: duplicates, reference and unknown entries. */
function segmentErrors(plan, runtimes) {
    const errors = [];
    const seen = new Set();
    for (const segment of plan.segments) {
        const key = segmentKey(segment);
        if (seen.has(key))
            errors.push({ code: "plan.duplicate-segment", segment });
        seen.add(key);
        const r = runtimes(segment);
        if (r?.reference === true)
            errors.push({ code: "plan.reference-entry", segment });
        else if (r?.runtimeSeconds === undefined && r?.plannedRuntimeSeconds === undefined)
            errors.push({ code: "plan.unknown-segment", segment });
    }
    return errors;
}
/**
 * The plan's summary: seconds used and remaining against the budget, and per segment which runtime
 * counted and what blocks its render. Does not refuse an overrun (ADR-032 decision 2). Throws
 * ProductionPlanInvalid for a duplicate, a reference entry, or a segment the pack does not know.
 */
export function summarisePlan(plan, runtimes, opts = {}) {
    const errors = segmentErrors(plan, runtimes);
    if (errors.length > 0)
        throw new ProductionPlanInvalid(errors);
    const budgetSeconds = opts.budgetSeconds ?? PRODUCTION_BUDGET_SECONDS;
    const segments = plan.segments.map((segment) => {
        const r = runtimes(segment) ?? {};
        const measured = r.runtimeSeconds !== undefined;
        return {
            packId: segment.packId,
            titleId: segment.titleId,
            seconds: r.runtimeSeconds ?? r.plannedRuntimeSeconds ?? 0,
            runtime: measured ? "measured" : "estimated",
            blocked: [...(opts.blocked?.(segment) ?? [])],
        };
    });
    const usedSeconds = segments.reduce((n, s) => n + s.seconds, 0);
    return { budgetSeconds, usedSeconds, remainingSeconds: budgetSeconds - usedSeconds, segments };
}
/** The seconds a stored plan uses today, counting only the segments the pack can still count. */
function storedSeconds(plan, runtimes) {
    let used = 0;
    for (const segment of plan.segments) {
        const r = runtimes(segment);
        if (r !== undefined && r.reference !== true)
            used += r.runtimeSeconds ?? r.plannedRuntimeSeconds ?? 0;
    }
    return used;
}
/**
 * The write-time check every writer calls before it stores a plan, so no surface can store an invalid
 * one: refuses a duplicate segment, a reference entry, a segment the pack does not know, and used
 * seconds over the budget (ADR-032 decision 1). A blocked segment is allowed; it is shown as blocked.
 * Given the stored plan as `opts.previous` while that plan is over the budget, a change that does not
 * increase usedSeconds is accepted even if it stays over; one that adds seconds must end within the
 * budget (ADR-032 decision 2). Without `previous`, the plain budget rule applies.
 */
export function validatePlan(plan, runtimes, opts = {}) {
    const errors = segmentErrors(plan, runtimes);
    if (errors.length > 0)
        return { ok: false, errors };
    const summary = summarisePlan(plan, runtimes, opts);
    const { usedSeconds, budgetSeconds } = summary;
    if (usedSeconds <= budgetSeconds)
        return { ok: true, summary };
    const previousUsedSeconds = opts.previous === undefined ? undefined : storedSeconds(opts.previous, runtimes);
    // The over-budget trap: a stored plan that is over may always come down, or stay level.
    if (previousUsedSeconds === undefined || previousUsedSeconds <= budgetSeconds)
        return { ok: false, errors: [{ code: "plan.over-budget", usedSeconds, budgetSeconds }] };
    if (usedSeconds <= previousUsedSeconds)
        return { ok: true, summary };
    return {
        ok: false,
        errors: [{ code: "plan.over-budget", usedSeconds, budgetSeconds, previousUsedSeconds }],
    };
}
//# sourceMappingURL=production-plan.js.map